import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  TrendingUp,
  Shield,
  FileCheck,
  Target,
  Building2,
  Users,
  Stethoscope,
  CheckCircle2,
} from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: TrendingUp,
      title: 'Revenue Cycle Optimization',
      description: 'End-to-end revenue cycle assessment and process improvement to accelerate cash flow and reduce leakage.',
    },
    {
      icon: Users,
      title: 'Coder & CDI Training and Education',
      description: 'Comprehensive training programs to elevate coder proficiency and CDI specialist effectiveness.',
    },
    {
      icon: Target,
      title: 'DRG Validation',
      description: 'Comprehensive DRG accuracy reviews to identify missed revenue and reduce compliance risk.',
    },
    {
      icon: FileCheck,
      title: 'Documentation Integrity (CDI Reviews)',
      description: 'Documentation improvement programs for CDI specialists and providers to capture true clinical complexity and acuity.',
    },
    {
      icon: Shield,
      title: 'Coding & CDI Denial Assistance',
      description: 'Expert support for coding-related denials and CDI query resolution to recover lost revenue.',
    },
    {
      icon: Stethoscope,
      title: 'Physician to Physician Collaboration',
      description: 'Clinical peer engagement to bridge documentation gaps and enhance provider understanding of reimbursement impact.',
    },
  ];

  const differentiators = [
    'Deep expertise in Medicare, Medicaid, and commercial payer policy',
    'Executive-level advisory approach',
    'Proven revenue optimization outcomes',
    'CDI and DRG validation expertise',
  ];

  const audiences = [
    { icon: Building2, name: 'Hospitals', description: 'Acute care facilities seeking reimbursement optimization' },
    { icon: Users, name: 'Health Systems', description: 'Multi-facility organizations requiring enterprise solutions' },
  ];

  const metrics = [
    { value: '42%', label: 'Average Denial Reduction' },
    { value: '$2.4M', label: 'Revenue Recovered Per Engagement' },
    { value: '98%', label: 'Audit Defense Success Rate' },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-texture bg-[#0F172A] relative overflow-hidden">
        <div className="container-custom relative z-10 py-24 sm:py-32 lg:py-40">
          <div className="max-w-4xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light text-white font-['Outfit'] animate-fade-in-up">
              Maximize Reimbursement.
              <br />
              <span className="text-[#0D9488]">Reduce Risk.</span>
              <br />
              Strengthen Compliance.
            </h1>
            <p className="mt-8 text-xl text-gray-300 max-w-2xl leading-relaxed animate-fade-in-up animation-delay-100">
              Strategic healthcare reimbursement consulting that transforms payer complexity into financial clarity. We help hospitals and health systems capture every dollar they've earned.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <Link to="/contact" data-testid="hero-cta-consultation">
                <Button className="bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md px-8 py-6 text-lg group">
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-cta-assessment">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0F172A] rounded-md px-8 py-6 text-lg">
                  Request an Assessment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differentiators.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-[#0D9488] flex-shrink-0 mt-0.5" />
                <span className="text-[#0F172A] font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Our Services
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit']">
              Comprehensive Revenue Cycle Solutions
            </h2>
            <p className="mt-4 text-lg text-[#64748B]">
              From payer strategy to compliance, we deliver end-to-end reimbursement optimization for healthcare organizations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-[#E2E8F0] p-8 card-hover"
                data-testid={`service-card-${index}`}
              >
                <div className="icon-container mb-6">
                  <service.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] font-['Outfit'] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services">
              <Button variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-md px-8 py-6 text-lg group">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-padding bg-white" data-testid="audience-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Who We Serve
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit']">
              Partnering with Healthcare Leaders
            </h2>
            <p className="mt-4 text-lg text-[#64748B]">
              We work directly with CFOs, Revenue Cycle VPs, Compliance Officers, and CDI Directors to drive measurable financial outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audiences.map((audience, index) => (
              <div
                key={index}
                className="text-center p-8 bg-[#FAFAFA] border border-[#E2E8F0] card-hover"
                data-testid={`audience-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[#0F172A] flex items-center justify-center mb-6">
                  <audience.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] font-['Outfit'] mb-3">
                  {audience.name}
                </h3>
                <p className="text-[#64748B]">{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results / Impact */}
      <section className="bg-[#0F172A] py-20" data-testid="metrics-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Proven Results
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-white font-['Outfit']">
              Impact That Matters
            </h2>
          </div>

          <div className="stats-grid border border-[#1e293b]">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className="text-center py-12 px-8 bg-[#0F172A]"
                data-testid={`metric-${index}`}
              >
                <div className="metric-value text-5xl sm:text-6xl text-[#0D9488] mb-4">
                  {metric.value}
                </div>
                <p className="text-gray-400 text-lg">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="container-custom">
          <div className="bg-white border border-[#E2E8F0] p-12 sm:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit'] mb-6">
              Let's Identify Your Reimbursement Opportunities
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto mb-8">
              Schedule a consultation with our team to discuss how we can help optimize your revenue cycle and strengthen your payer strategy.
            </p>
            <Link to="/contact" data-testid="cta-banner-consultation">
              <Button className="bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md px-10 py-6 text-lg group">
                Start the Conversation
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
